import { Link } from 'react-router-dom';
import { useBalance, useDisplayRate } from '../api/useBilling.ts';
import { useKeysList } from '../api/useKeys.ts';
import { useUsageByKey, useUsageHourly, useUsageSummary } from '../api/useUsage.ts';
import { GiftBalanceLine } from '../components/GiftBalanceLine.tsx';
import { Skeleton } from '../components/Skeleton.tsx';
import {
  formatTimestamp,
  formatTokens,
  formatUsdMicrosOrCentsAsCnyDisplay,
  formatUsdMicrosOrCentsAsCnyMetered,
  usdMicrosFromCentsFallback,
} from '../components/formatters.ts';
import { hasPermission, useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import { Chip, KpiCard, PageHeader, Panel, TBody, Table, Td, Th, THead, Tr } from '../ui/index.ts';

/**
 * Tenant dashboard — first surface after sign-in.
 *
 * Layout (Field Notes, epic #950): serif page header, four `KpiCard`
 * stats (balance, this-month cost, active keys count, last request
 * timestamp) + recent-activity and top-keys `Panel`s over responsive
 * `Table`s.
 *
 * We deliberately read multiple endpoints in parallel — React Query
 * handles the concurrency + loading state individually. A stat card
 * stays on `—` until its own query resolves; one slow endpoint does
 * not block the rest of the dashboard.
 *
 * The "last request" value is derived from the most-recent non-empty
 * hourly bucket. Platform-api does not yet surface a direct "last
 * request" timestamp, and adding one would duplicate usage accounting
 * logic; the derived value is good enough for a glance-pace read.
 */

function firstOfMonthIso(): string {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
  return start.toISOString();
}

function nextMonthStartIso(): string {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() + 1, 1, 0, 0, 0, 0);
  return start.toISOString();
}

const HINT_LINK_CLASS = 'font-medium text-forest hover:text-forest-bright';

export function Dashboard(): JSX.Element {
  const t = useT();
  const { user } = useSession();
  const canReadBilling = hasPermission(user, 'org:billing:read');
  const balanceQ = useBalance({ enabled: canReadBilling });
  const displayRateQ = useDisplayRate();
  const usageQ = useUsageSummary({ from: firstOfMonthIso(), to: nextMonthStartIso() });
  const keysQ = useKeysList();
  const hourlyQ = useUsageHourly(7);
  const byKeyQ = useUsageByKey({ from: firstOfMonthIso(), to: nextMonthStartIso() });

  const activeKeys = keysQ.data?.keys.filter((k) => k.revokedAt === null).length ?? null;
  const cnyPerUsd = displayRateQ.data?.cnyPerUsd ?? null;
  const balanceUsdMicros =
    balanceQ.data !== undefined
      ? usdMicrosFromCentsFallback(balanceQ.data.balanceUsdMicros, balanceQ.data.balanceUsdCents)
      : null;
  const canManageBilling = hasPermission(user, 'org:billing:manage');

  const lastBucket =
    (hourlyQ.data?.buckets ?? [])
      .filter((b) => b.requests > 0)
      .sort((a, b) => b.hour.localeCompare(a.hour))[0] ?? null;
  const lastRequestAt = lastBucket?.hour ?? null;

  const cards: readonly {
    labelKey: I18nKey;
    value: string;
    hint: JSX.Element;
    testId: string;
  }[] = [
    {
      labelKey: 'dashboard.stat.balance.label',
      value:
        canReadBilling && balanceQ.data !== undefined
          ? formatUsdMicrosOrCentsAsCnyDisplay(
              balanceQ.data.balanceUsdMicros,
              balanceQ.data.balanceUsdCents,
              cnyPerUsd,
            )
          : '—',
      hint:
        !canReadBilling ? (
          <span>{t('dashboard.stat.balance.zero_readonly')}</span>
        ) : balanceQ.data !== undefined ? (
          (balanceUsdMicros ?? 0) > 0 ? (
            <span className="flex flex-col gap-0.5">
              <span>
                {t('dashboard.stat.balance.updated', {
                  timestamp: formatTimestamp(balanceQ.data.updatedAt),
                })}
              </span>
              {/* Which slice of that number was given, and when it goes away. */}
              <GiftBalanceLine
                balance={balanceQ.data}
                cnyPerUsd={cnyPerUsd}
                testId="dashboard-stat-balance-gift"
              />
            </span>
          ) : canManageBilling ? (
            <Link to="/billing/topup" className={HINT_LINK_CLASS}>
              {t('dashboard.stat.balance.zero_cta')}
            </Link>
          ) : (
            <span>{t('dashboard.stat.balance.zero_readonly')}</span>
          )
        ) : balanceQ.isError ? (
          <span>{t('dashboard.stat.balance.unavailable')}</span>
        ) : (
          <span>{t('dashboard.stat.balance.loading')}</span>
        ),
      testId: 'dashboard-stat-balance',
    },
    {
      labelKey: 'dashboard.stat.cost.label',
      value:
        usageQ.data !== undefined
          ? formatUsdMicrosOrCentsAsCnyDisplay(
              usageQ.data.totalCostUsdMicros,
              usageQ.data.totalCostUsdCents,
              cnyPerUsd,
            )
          : '—',
      hint:
        usageQ.data !== undefined ? (
          <span>
            {t('dashboard.stat.cost.tokens', {
              tokens: formatTokens(usageQ.data.totalInputTokens + usageQ.data.totalOutputTokens),
            })}
          </span>
        ) : usageQ.isError ? (
          <span>{t('dashboard.stat.cost.unavailable')}</span>
        ) : (
          <span>{t('dashboard.stat.cost.loading')}</span>
        ),
      testId: 'dashboard-stat-cost',
    },
    {
      labelKey: 'dashboard.stat.keys.label',
      value: activeKeys !== null ? String(activeKeys) : '—',
      hint: keysQ.isError ? (
        <span>{t('dashboard.stat.keys.unavailable')}</span>
      ) : activeKeys === 0 ? (
        <Link to="/keys" className={HINT_LINK_CLASS}>
          {t('dashboard.stat.keys.empty_cta')}
        </Link>
      ) : (
        <span>{t('dashboard.stat.keys.description')}</span>
      ),
      testId: 'dashboard-stat-keys',
    },
    {
      labelKey: 'dashboard.stat.last_request.label',
      value:
        lastRequestAt !== null
          ? formatTimestamp(lastRequestAt)
          : t('dashboard.stat.last_request.empty'),
      hint:
        lastRequestAt !== null ? (
          <span>{t('dashboard.stat.last_request.hint')}</span>
        ) : (
          <Link to="/keys" className={HINT_LINK_CLASS}>
            {t('dashboard.stat.last_request.empty_cta')}
          </Link>
        ),
      testId: 'dashboard-stat-last-request',
    },
  ];

  return (
    <section data-testid="dashboard-page" className="flex flex-col gap-8">
      <PageHeader title={t('dashboard.title')} sub={t('dashboard.subtitle')} />

      <div
        data-testid="dashboard-stat-grid"
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4"
      >
        {cards.map((card) => (
          <KpiCard
            key={card.testId}
            testId={card.testId}
            label={t(card.labelKey)}
            value={card.value}
            delta={{ text: card.hint, tone: 'neutral' }}
          />
        ))}
      </div>

      <Panel
        caption={t('dashboard.activity.title')}
        testId="dashboard-activity"
        padded
        actions={
          <Link to="/usage" data-testid="dashboard-activity-link" className={HINT_LINK_CLASS}>
            {t('dashboard.activity.view_all')}
          </Link>
        }
      >
        <DashboardActivity
          buckets={(hourlyQ.data?.buckets ?? []).slice(-10).reverse()}
          isLoading={hourlyQ.isLoading}
          isError={hourlyQ.isError}
          cnyPerUsd={cnyPerUsd}
        />
      </Panel>

      <Panel
        caption={t('dashboard.top_keys.title')}
        testId="dashboard-top-keys"
        padded
        actions={
          <Link to="/keys" data-testid="dashboard-top-keys-link" className={HINT_LINK_CLASS}>
            {t('dashboard.top_keys.manage')}
          </Link>
        }
      >
        <DashboardTopKeys
          entries={aggregateTopKeys(byKeyQ.data?.keys ?? []).slice(0, 5)}
          isLoading={byKeyQ.isLoading}
          isError={byKeyQ.isError}
          cnyPerUsd={cnyPerUsd}
        />
      </Panel>
    </section>
  );
}

interface ActivityProps {
  readonly buckets: readonly {
    hour: string;
    requests: number;
    inputTokens: number;
    outputTokens: number;
    costUsdCents: number;
    costUsdMicros?: number;
    costUsd: string;
  }[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly cnyPerUsd: number | null;
}

function DashboardActivity({
  buckets,
  isLoading,
  isError,
  cnyPerUsd,
}: ActivityProps): JSX.Element {
  const t = useT();
  if (isLoading) {
    return (
      <div className="flex flex-col gap-2" data-testid="dashboard-activity-loading">
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-8" />
        ))}
        <span className="sr-only">{t('dashboard.activity.loading')}</span>
      </div>
    );
  }
  if (isError) {
    return (
      <p className="text-body text-bad" role="alert">
        {t('dashboard.activity.error')}
      </p>
    );
  }
  if (buckets.length === 0) {
    return (
      <div className="flex flex-col gap-2 text-body text-ink-low">
        <p>{t('dashboard.activity.empty')}</p>
        <Link to="/keys" className={HINT_LINK_CLASS}>
          {t('dashboard.activity.empty_cta')}
        </Link>
      </div>
    );
  }
  return (
    <div data-testid="dashboard-activity-table">
      <Table className="border-0 bg-transparent max-md:!bg-transparent">
        <THead>
          <Tr interactive={false}>
            <Th>{t('dashboard.activity.column.hour')}</Th>
            <Th num>{t('dashboard.activity.column.requests')}</Th>
            <Th num>{t('dashboard.activity.column.tokens')}</Th>
            <Th num>{t('dashboard.activity.column.cost')}</Th>
          </Tr>
        </THead>
        <TBody>
          {buckets.map((b) => (
            <Tr key={b.hour} data-testid={`dashboard-activity-row-${b.hour}`}>
              <Td mono heading>
                {formatTimestamp(b.hour)}
              </Td>
              <Td num label={t('dashboard.activity.column.requests')}>
                {formatTokens(b.requests)}
              </Td>
              <Td num label={t('dashboard.activity.column.tokens')}>
                {formatTokens(b.inputTokens + b.outputTokens)}
              </Td>
              <Td num emphasis label={t('dashboard.activity.column.cost')}>
                {formatUsdMicrosOrCentsAsCnyMetered(b.costUsdMicros, b.costUsdCents, cnyPerUsd)}
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>
    </div>
  );
}

interface TopKeyAggregate {
  readonly apiKeyId: string;
  readonly apiKeyName: string | null;
  readonly apiKeyPrefix: string | null;
  readonly requests: number;
  readonly costUsdMicros: number;
}

/**
 * `/v1/usage/by-key` rows are one per key×SKU since epic #898 P6
 * (#913); the "top keys" panel keeps its per-KEY semantics by folding
 * the rows back down to distinct `apiKeyId` before ranking by spend.
 */
function aggregateTopKeys(
  rows: readonly {
    apiKeyId: string;
    apiKeyName: string | null;
    apiKeyPrefix: string | null;
    requests: number;
    costUsdCents: number;
    costUsdMicros?: number;
  }[],
): readonly TopKeyAggregate[] {
  const byKey = new Map<string, TopKeyAggregate>();
  for (const row of rows) {
    const existing = byKey.get(row.apiKeyId);
    byKey.set(row.apiKeyId, {
      apiKeyId: row.apiKeyId,
      apiKeyName: existing?.apiKeyName ?? row.apiKeyName,
      apiKeyPrefix: existing?.apiKeyPrefix ?? row.apiKeyPrefix,
      requests: (existing?.requests ?? 0) + row.requests,
      costUsdMicros:
        (existing?.costUsdMicros ?? 0) +
        usdMicrosFromCentsFallback(row.costUsdMicros, row.costUsdCents),
    });
  }
  return [...byKey.values()].sort((a, b) => b.costUsdMicros - a.costUsdMicros);
}

interface TopKeysProps {
  readonly entries: readonly TopKeyAggregate[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly cnyPerUsd: number | null;
}

function DashboardTopKeys({
  entries,
  isLoading,
  isError,
  cnyPerUsd,
}: TopKeysProps): JSX.Element {
  const t = useT();
  if (isLoading) {
    return (
      <div className="flex flex-col gap-2" data-testid="dashboard-top-keys-loading">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-8" />
        ))}
        <span className="sr-only">{t('dashboard.top_keys.loading')}</span>
      </div>
    );
  }
  if (isError) {
    return (
      <p className="text-body text-bad" role="alert">
        {t('dashboard.top_keys.error')}
      </p>
    );
  }
  if (entries.length === 0) {
    return (
      <div className="flex flex-wrap items-center gap-3">
        <Chip tone="neutral">{t('dashboard.top_keys.empty.chip')}</Chip>
        <span className="text-body text-ink-low">{t('dashboard.top_keys.empty.hint')}</span>
        <Link to="/keys" className={HINT_LINK_CLASS}>
          {t('dashboard.top_keys.empty_cta')}
        </Link>
      </div>
    );
  }
  return (
    <div data-testid="dashboard-top-keys-table">
      <Table className="border-0 bg-transparent max-md:!bg-transparent">
        <THead>
          <Tr interactive={false}>
            <Th>{t('dashboard.top_keys.column.key')}</Th>
            <Th num>{t('dashboard.top_keys.column.requests')}</Th>
            <Th num>{t('dashboard.top_keys.column.cost')}</Th>
          </Tr>
        </THead>
        <TBody>
          {entries.map((k) => (
            <Tr key={k.apiKeyId} data-testid={`dashboard-top-key-${k.apiKeyId}`}>
              <Td emphasis heading>
                {keyDisplayName(k.apiKeyName, k.apiKeyPrefix, t)}
              </Td>
              <Td num label={t('dashboard.top_keys.column.requests')}>
                {formatTokens(k.requests)}
              </Td>
              <Td num emphasis label={t('dashboard.top_keys.column.cost')}>
                {formatUsdMicrosOrCentsAsCnyMetered(k.costUsdMicros, 0, cnyPerUsd)}
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>
    </div>
  );
}

function keyDisplayName(
  name: string | null,
  prefix: string | null,
  t: (key: I18nKey, vars?: Record<string, string | number>) => string,
): string {
  if (typeof name === 'string' && name.length > 0) return name;
  if (typeof prefix === 'string' && prefix.length > 0) return t('usage.by_key.unnamed_key');
  return t('usage.by_key.unknown_key');
}
